import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICallBackRow,
  ICallQueueOverview,
  ICallSheet,
  IDecideOfferApprovalRequest,
  ILogCallOutcomeRequest,
  IPendingApprovalRow,
  IRequestOfferApprovalRequest,
  UpNextFilter,
} from "../models/CallQueue";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

const overviewKey = ["callQueue", "overview"];

// The live queue. Refusals come back 200 with success=false and a message for the agent, so every
// caller checks `success` rather than relying on the request throwing.
export default function useCallQueue(filter: UpNextFilter = UpNextFilter.AllDue, enabled = true, componentName = "useCallQueue") {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const overview = useQuery({
    queryKey: [...overviewKey, filter],
    queryFn: () => get<DefaultResponseWithData<ICallQueueOverview>>(`/callqueue?filter=${filter}&take=20`),
    enabled,
    refetchInterval: 1000 * 30,
    refetchIntervalInBackground: false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries(overviewKey);
    queryClient.invalidateQueries(["callQueue", "callbacks"]);
    queryClient.invalidateQueries(["callQueue", "approvals"]);
    queryClient.invalidateQueries(["quotes", "stats"]);
  };

  const serveNext = useMutation(
    () => post<undefined, DefaultResponseWithData<ICallSheet>>("/callqueue/next", undefined),
    { onSettled: invalidate }
  );

  const openLead = useMutation(
    (leadId: string) => post<undefined, DefaultResponseWithData<ICallSheet>>(`/callqueue/${leadId}/open`, undefined),
    { onSettled: invalidate }
  );

  const refreshSheet = useMutation(
    (leadId: string) => get<DefaultResponseWithData<ICallSheet>>(`/callqueue/${leadId}`)
  );

  const logOutcome = useMutation(
    ({ leadId, body }: { leadId: string; body: ILogCallOutcomeRequest }) =>
      post<ILogCallOutcomeRequest, DefaultResponse>(`/callqueue/${leadId}/outcome`, body),
    {
      onSettled: () => {
        invalidate();
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const requestApproval = useMutation(
    ({ leadId, body }: { leadId: string; body: IRequestOfferApprovalRequest }) =>
      post<IRequestOfferApprovalRequest, DefaultResponse>(`/callqueue/${leadId}/approval`, body),
    { onSettled: invalidate }
  );

  // The heartbeat: keeps the lead with this agent while a call is going on.
  const renewHold = useMutation(
    (leadId: string) => post<undefined, DefaultResponse>(`/callqueue/${leadId}/hold`, undefined)
  );

  const refreshCatPrice = useMutation(
    (leadId: string) => post<undefined, DefaultResponseWithData<ICallSheet>>(`/callqueue/${leadId}/cat-price`, undefined)
  );

  return {
    overview,
    serveNext,
    openLead,
    refreshSheet,
    logOutcome,
    requestApproval,
    refreshCatPrice,
    renewHold,
  };
}

export function useCallBacks(mineOnly: boolean, componentName = "useCallBacks") {
  const { get } = useApiHelper(apiConfig(componentName));

  const callBacks = useQuery({
    queryKey: ["callQueue", "callbacks", mineOnly],
    queryFn: () => get<DefaultResponseWithData<ICallBackRow[]>>(`/callqueue/callbacks?mineOnly=${mineOnly}`),
    refetchInterval: 1000 * 60,
  });

  return { callBacks };
}

export function useOfferApprovals(enabled: boolean, withList = true, componentName = "useOfferApprovals") {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const pendingCount = useQuery({
    queryKey: ["callQueue", "approvals", "count"],
    queryFn: () => get<DefaultResponseWithData<number>>("/callqueue/approvals/count"),
    enabled,
    refetchInterval: 1000 * 60,
  });

  const pending = useQuery({
    queryKey: ["callQueue", "approvals", "list"],
    queryFn: () => get<DefaultResponseWithData<IPendingApprovalRow[]>>("/callqueue/approvals"),
    enabled: enabled && withList,
  });

  const decide = useMutation(
    ({ approvalId, body }: { approvalId: string; body: IDecideOfferApprovalRequest }) =>
      post<IDecideOfferApprovalRequest, DefaultResponse>(`/callqueue/approvals/${approvalId}/decision`, body),
    {
      onSettled: () => {
        queryClient.invalidateQueries(["callQueue"]);
        queryClient.invalidateQueries(["quotes", "stats"]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  return { pendingCount, pending, decide };
}
